import { DropdownMenu, Heading } from '@radix-ui/themes';
import clsx from 'clsx';
import {
  AtSign,
  Columns,
  File,
  MessageCircle,
  Palette,
  PhoneOutgoing,
  Plus,
  Settings,
  Sparkles,
  Users,
} from 'lucide-react';
import posthog from 'posthog-js';
import { Separator } from 'radix-ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router';
import { TEMPLATE_PERMISSIONS } from '@hubertai/screening-question-types';
import { identifyPosthogUser } from 'utils/identifyPosthogUser';
import { fetchManagerAccessToken } from 'app/tokens';
import { consumeSkipAutoLogin } from 'app/sessionIdentity';
import { runManagerAuthBootstrap } from 'app/managerAuthBootstrap';
import DialogsContainer from 'components/Dialogs';
import { HubertButton } from 'components/HubertUI/HubertButton';
import NavbarDropdown from 'components/NavBar/NavbarDropdown';
import NotificationsPopover from 'components/Notifications/NotificationsPopover';
import { NotificationsContextProvider } from 'components/Notifications/notifications.context';
import { usePageTopBar } from 'components/PageTopBar/page-top-bar.context';
import { ShowIfUserIsInUserGroup } from 'components/Questionsv2/builder/ShowIfUserIsInUserGroup';
import { TemplateBuilderContextProvider } from 'components/Templates/builder/context/template-builder.context';
import HubertLogo from 'components/common/Logo/HubertLogo';
import HubertLogoName from 'components/common/Logo/HubertLogoName';
import MutedText from 'components/common/MutedText';
import { Button } from 'components/ui/button';
import { setIsLoggedIn, setIsLoggedOut } from 'rtk/ConfigSlice';
import { toggleCreateTemplateDialog, toggleCreateClientDialog } from 'rtk/layout/LayoutControlSlice';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import { EUserType, UserGroup } from 'types/accessControll.types';
import { isHubertAdminUser, isRecruiterUser } from 'types/user.types';
import type { TAdminUser, TRecruiterAppUser } from 'types/user.types';

let eventId = 0;

const MainLayout = () => {
  const { t, i18n } = useTranslation('NavBar');
  const user = useAppSelector((state) => state.config.user);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { stringTitle, nodeTitle } = usePageTopBar();

  const links = useMemo(
    () => [
      {
        id: 'clients',
        title: t('sidebar.clients'),
        href: '/clients',
        position: 'top',
        icon: <AtSign />,
        authorized: [EUserType.HUBERT],
      },
      {
        id: 'jobs',
        title: t('sidebar.jobs'),
        href: '/jobs',
        position: 'top',
        icon: <File />,
        authorized: [
          EUserType.HUBERT,
          // EUserType.RECRUITER
        ],
      },
      {
        id: 'candidates',
        title: t('sidebar.candidates'),
        href: '/candidates',
        position: 'top',
        icon: <Users />,
        authorized: [
          EUserType.HUBERT,
          // EUserType.RECRUITER
        ],
      },
      {
        id: 'outreach',
        title: 'Outreach',
        href: '/outreach/campaigns',
        position: 'top',
        icon: <PhoneOutgoing />,
        authorized: [EUserType.HUBERT],
      },
      {
        id: 'templates',
        title: t('sidebar.templates'),
        href: '/templates',
        position: 'top',
        icon: <Columns />,
        authorized: [EUserType.HUBERT, EUserType.RECRUITER],
      },
      {
        id: 'questions',
        title: t('sidebar.questions'),
        href: '/questions',
        position: 'top',
        icon: <MessageCircle />,
        authorized: [
          EUserType.HUBERT,
          // EUserType.RECRUITER
        ],
      },
      {
        id: 'themes',
        title: t('sidebar.themes'),
        href: '/themes',
        position: 'top',
        icon: <Palette />,
        authorized: [EUserType.HUBERT],
      },
      {
        id: 'settings',
        title: t('sidebar.settings'),
        href: '/settings',
        position: 'bottom',
        icon: <Settings />,
        authorized: [
          EUserType.HUBERT,
          // EUserType.RECRUITER
        ],
      },
    ],
    [t],
  );
  const [isLoadingAuth, setIsLoadingAuth] = useState<boolean>(true);
  const [authError, setAuthError] = useState<string | null>(null);

  const loc = useLocation();
  const { pathname } = loc;
  const pathnameRef = useRef(pathname);
  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  const [searchParams] = useSearchParams();
  const [isEmbedded, setIsEmbedded] = useState(searchParams.get('embedded') === '1');

  if (!isEmbedded && window.self != window.top) setIsEmbedded(true);
  useEffect(() => {
    return runManagerAuthBootstrap({ consumeSkipAutoLogin, fetchManagerAccessToken }, (outcome) => {
      if (outcome.type === 'authenticated') {
        dispatch(setIsLoggedIn({ user: outcome.user }));
        // Flags must be evaluated for this user before anything renders that reads them.
        void identifyPosthogUser(outcome.user as TRecruiterAppUser | TAdminUser).then(() => setIsLoadingAuth(false));
        return;
      }

      dispatch(setIsLoggedOut());
      posthog.unregister('user_email');
      if (isEmbedded) {
        if (outcome.type === 'mismatch') {
          window.parent.postMessage({ event: 'session-invalid' }, '*');
        } else {
          console.error(`fetchManagerAccessToken Error: `, outcome.error);
          setAuthError('Something went wrong loading this page. Please try again.');
          setIsLoadingAuth(false);
        }
      } else {
        navigate(`/login?redirect=${pathnameRef.current}`);
      }
    });
  }, [dispatch, navigate, isEmbedded]);

  useEffect(() => {
    if (isEmbedded)
      window.parent.postMessage(
        { event: 'navigated', eventId: ++eventId, pathname: loc.pathname, search: loc.search },
        { targetOrigin: '*' },
      );
  }, [isEmbedded, loc]);

  useEffect(() => {
    if (!isEmbedded) return;
    const handler = (e: MessageEvent) => {
      if (e.data?.event === 'languageChanged' && e.data.language) {
        i18n.changeLanguage(e.data.language);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [isEmbedded, i18n]);

  if (isLoadingAuth) {
    return null;
  }

  if (authError) {
    return (
      <div className="flex h-full items-center justify-center">
        <MutedText>{authError}</MutedText>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const breakAtIndex = links.findLastIndex((link) => link.position === 'top');

  const userCanCreateTemplate =
    isHubertAdminUser(user) ||
    user.userRoles?.includes('recruiter-admin') ||
    user.access_groups_permissions?.some((ag) => ag.permissions.includes(TEMPLATE_PERMISSIONS.Create));

  const goToAgentChat = () => {
    navigate('/agent-chat');
  };
  return (
    <NotificationsContextProvider>
      <TemplateBuilderContextProvider>
        {!isEmbedded && (
          <nav
            role="navigation"
            aria-label={t('sidebar.mainNavigation')}
            className={clsx(
              'group absolute z-10 h-full w-[50px]',
              'py-6',
              'px-1',
              'bg-primary',
              'dark:bg-black',
              'hover:w-[211px]',
              'transition-all duration-300 ease-out',
            )}
          >
            <ul
              className={clsx('flex flex-col gap-6', 'h-full', 'max-h-full', 'items-start', 'group-hover:items-start')}
            >
              <li className="w-full">
                <Link
                  className={clsx(
                    'flex',
                    'text-foreground dark:text-muted-foreground',
                    'text-gray-50',
                    'cursor-pointer',
                    'w-full pl-1',
                  )}
                  to={isRecruiterUser(user) ? '/templates' : '/'}
                >
                  <HubertLogoName width={120} className="hidden cursor-pointer group-hover:block" />
                  <HubertLogo width={38} className="ml-[-0px] cursor-pointer group-hover:hidden" />
                </Link>
              </li>
              {links.map((link, idx) => {
                if (!link.authorized.includes(user?.userType)) {
                  return null;
                }
                return (
                  <li
                    key={`sidebar-toplink-${link.href}`}
                    className={clsx('w-full', idx === breakAtIndex ? 'grow' : '')}
                  >
                    <NavLink
                      className={({ isActive }) =>
                        clsx(
                          'flex justify-center group-hover:justify-start',
                          'rounded-md p-2 group-hover:pl-3',
                          isActive ? 'bg-primary-800 text-gray-100' : 'text-gray-300',
                          'cursor-pointer',
                          'hover:text-gray-100',
                        )
                      }
                      to={{
                        pathname: link.href,
                      }}
                    >
                      <div className="flex justify-center">
                        <div>{link.icon}</div>
                        <div className="hidden group-hover:block">
                          <span className="hubert-vertical-center ml-2">{link.title}</span>
                        </div>
                      </div>
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </nav>
        )}
        <header
          className={clsx(
            'top-0 right-0 left-0 h-[64px]',
            'z-[1]',
            !isEmbedded && 'ml-[50px]',
            'border-b border-gray-200',
          )}
        >
          <div
            className={clsx(
              'h-full',
              'flex justify-between',
              'pr-[24px] pl-[24px]',
              'hubert-vertical-center',
              'w-full',
            )}
          >
            <div className="w-full">
              {(() => {
                if (nodeTitle) {
                  return nodeTitle;
                } else if (stringTitle) {
                  return <Heading size={'3'}>{stringTitle}</Heading>;
                } else return null;
              })()}
            </div>

            <Separator.Root
              className={clsx(
                'mx-[24px]',
                'bg-gray-300',
                'data-[orientation=vertical]:h-[65%]',
                'data-[orientation=vertical]:w-px',
              )}
              decorative
              orientation="vertical"
            />
            {isHubertAdminUser(user) && (
              <Button
                variant="ghost"
                size="icon"
                className="mr-2 size-8"
                onClick={goToAgentChat}
                aria-label="Open agent chat"
              >
                <Sparkles className={`h-4 w-4`} />
              </Button>
            )}
            <div className="hubert-vertical-center flex flex-row justify-between gap-3">
              <DropdownMenu.Root>
                <DropdownMenu.Trigger>
                  <HubertButton icon={<Plus className="h-4 w-4" />} size={'default'}>
                    {t('createDropdown.button')}
                  </HubertButton>
                </DropdownMenu.Trigger>

                <NotificationsPopover />

                <DropdownMenu.Content className="p-2">
                  <div className="pr-2 pl-2 text-sm font-semibold">{t('createDropdown.heading')}</div>

                  <DropdownMenu.Separator />

                  <DropdownMenu.Item
                    disabled={!userCanCreateTemplate}
                    onClick={() => {
                      if (userCanCreateTemplate) {
                        dispatch(
                          toggleCreateTemplateDialog({
                            show: true,
                            data: {},
                          }),
                        );
                      }
                    }}
                  >
                    {t('createDropdown.createTemplate')}
                  </DropdownMenu.Item>
                  <ShowIfUserIsInUserGroup group={UserGroup.HUBERT}>
                    <DropdownMenu.Item
                      onClick={() => {
                        dispatch(
                          toggleCreateClientDialog({
                            show: true,
                          }),
                        );
                      }}
                    >
                      {t('createDropdown.createClient')}
                    </DropdownMenu.Item>
                  </ShowIfUserIsInUserGroup>
                </DropdownMenu.Content>
              </DropdownMenu.Root>
              {!isEmbedded && <NavbarDropdown />}
            </div>
          </div>
        </header>
        <main
          id={'main-content'}
          className={clsx(
            'flex-1',
            !isEmbedded && 'ml-[50px]',
            'max-w-screen',
            'max-h-[calc(100vh-var(--navBarVisibleHeight))]',
            'h-[calc(100vh-var(--navBarVisibleHeight))]',
            'overflow-auto',
          )}
        >
          <DialogsContainer />
          <Outlet />
        </main>
      </TemplateBuilderContextProvider>
    </NotificationsContextProvider>
  );
};

export default MainLayout;
